import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID, effect, HostListener } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PrimeNG } from 'primeng/config';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AppSettings } from './services/settings/app-settings.service';
import { AuthService } from './services/auth/auth.service';
import { AuthTokenMonitorService } from './core/auth/token-monitor.service';
import { SessionExpiryDialogComponent } from './components/global/session-expiry-dialog/session-expiry-dialog.component';
import { DelegatedSessionDialogComponent } from './components/global/session-expiry-dialog/delegated-session-dialog.component';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: false,
  providers: [AuthService, DialogService],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'mb-web-administrator';

  private sessionDialogRef: DynamicDialogRef | null = null;
  private subscriptions = new Subscription();

  constructor(
    private primeng: PrimeNG,
    public appSettings: AppSettings,
    private tokenMonitor: AuthTokenMonitorService,
    private dialogService: DialogService,
    private router: Router,
    private messageService: MessageService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Efecto para mostrar/ocultar el diálogo de advertencia
    if (isPlatformBrowser(this.platformId)) {
      effect(() => {
        const showWarning = this.tokenMonitor.showWarning();
        this.handleWarningChange(showWarning);
      });

      // Efecto para mostrar/ocultar el diálogo de sesión delegada
      effect(() => {
        const showDelegatedWarning = this.tokenMonitor.showDelegatedWarning();
        this.handleDelegatedWarningChange(showDelegatedWarning);
      });
    }
  }

  ngOnInit() {
    this.primeng.ripple.set(true);

    if (isPlatformBrowser(this.platformId)) {
      // Suscribirse a eventos de sesión
      this.subscriptions.add(
        this.tokenMonitor.sessionExpired$.subscribe(() => {
          this.closeAllDialogs();
        })
      );

      this.subscriptions.add(
        this.tokenMonitor.sessionExtended$.subscribe(() => {
          // Cerrar diálogo de advertencia si está abierto
          this.closeSessionDialog();
        })
      );

      // Verificar si la URL indica que una sesión de soporte expiró
      this.checkSupportSessionExpired();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.closeSessionDialog();
  }

  private handleWarningChange(showWarning: boolean): void {
    if (showWarning && !this.sessionDialogRef) {
      this.openSessionExpiryDialog();
    } else if (!showWarning && this.sessionDialogRef) {
      this.closeSessionDialog();
    }
  }

  private handleDelegatedWarningChange(showDelegated: boolean): void {
    if (showDelegated && !this.sessionDialogRef) {
      this.openDelegatedSessionDialog();
    } else if (!showDelegated && this.sessionDialogRef) {
      this.closeSessionDialog();
    }
  }

  private openDelegatedSessionDialog(): void {
    if (this.sessionDialogRef) return;

    this.sessionDialogRef = this.dialogService.open(DelegatedSessionDialogComponent, {
      header: 'Sesión Delegada',
      width: '500px',
      modal: true,
      closable: false,
      dismissableMask: false,
      styleClass: 'delegated-session-modal',
    });

    this.sessionDialogRef?.onClose.subscribe((result: any) => {
      this.sessionDialogRef = null;
    });
  }

  private openSessionExpiryDialog(): void {
    if (this.sessionDialogRef) return;

    this.sessionDialogRef = this.dialogService.open(SessionExpiryDialogComponent, {
      header: 'Advertencia de Sesión',
      width: '450px',
      modal: true,
      closable: false,
      dismissableMask: false,
      styleClass: 'session-expiry-modal',
    });

    this.sessionDialogRef?.onClose.subscribe((result: any) => {
      this.sessionDialogRef = null;
      // El componente del diálogo maneja las acciones
    });
  }

  private closeSessionDialog(): void {
    if (this.sessionDialogRef) {
      this.sessionDialogRef.close();
      this.sessionDialogRef = null;
    }
  }

  /**
   * Cierra todos los diálogos abiertos cuando la sesión expira
   */
  private closeAllDialogs(): void {
    // Cerrar el diálogo de sesión
    this.closeSessionDialog();

    // Intentar cerrar otros diálogos de PrimeNG que puedan estar abiertos
    try {
      // Los DynamicDialogs se cierran automáticamente al destruir el DialogService
      // pero podemos emitir un evento global para que otros componentes reaccionen
      const event = new CustomEvent('mb:sessionExpired');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(event);
      }
    } catch {}
  }

  /**
   * Ctrl+Shift+1 → forzar diálogo de expiración de sesión (dev testing)
   */
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (event.ctrlKey && event.shiftKey && event.code === 'Digit1') {
      event.preventDefault();
      event.stopPropagation();
      this.tokenMonitor.forceShowExpiryWarning();
    }
  }

  /**
   * Detecta si la URL contiene el parámetro supportSession=expired, muestra un mensaje
   * informativo y limpia el parámetro para evitar notificaciones repetidas.
   */
  private checkSupportSessionExpired(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const currentUrlTree = this.router.parseUrl(this.router.url);
    const queryParams = { ...currentUrlTree.queryParams };

    if (queryParams['supportSession'] === 'expired') {
      this.messageService.add({
        severity: 'info',
        summary: 'Sesión delegada finalizada',
        detail: 'La sesión delegada en el espacio del cliente expiró. Si necesitas continuar, vuelve a abrir el enlace \"Ir a grupo\".',
        life: 6000,
      });

      delete queryParams['supportSession'];

      this.router.navigate([], {
        queryParams,
        replaceUrl: true,
      });
    }
  }
}
